
import React from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Instagram, Mail, PhoneCall } from 'lucide-react';
import { Button } from '@/components/ui/button';

const quickLinks = [
  { id: 'services', label: 'Solutions' },
  { id: 'how-we-work', label: 'Process' },
  { id: 'who-we-help', label: 'Engineering' },
  { id: 'legal-services', label: 'Legal' },
  { id: 'contact', label: 'Contact' },
];

// Scrolls smoothly to the emoji in each section
const scrollToSection = (sectionId: string) => {
  const section = document.getElementById(sectionId);
  if (section) {
    const navbarHeight = 64; // Consistent with the navbar height
    
    // Find the emoji element within the section
    const emojiElement = section.querySelector('.text-xl, .text-2xl');
    
    if (emojiElement) {
      // Calculate position of the emoji element
      const emojiPosition = emojiElement.getBoundingClientRect().top + window.pageYOffset;
      
      // Add a small offset for better positioning
      const scrollPosition = emojiPosition - navbarHeight - 20;
      
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    } else {
      // Fallback to the section top if emoji not found
      const offsetTop = section.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  }
};

const Footer = () => (
  <footer className="bg-[#001233] shadow-sm py-10 border-t border-white/10">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Logo and about section */}
        <div className="flex flex-col">
          <div className="mb-4">
            <img 
              src="/lovable-uploads/c7b39752-6d09-4df6-b424-5dee5512788f.png"
              alt="NextGen Startup Logo"
              className="h-8 w-auto object-contain"
            />
          </div>
          <p className="text-white/80 text-sm mt-2">
            Transforming business ideas into reality with AI-powered tools and expert strategies.
          </p>
          <div className="flex items-center space-x-4 mt-4">
            <a 
              href="https://www.instagram.com/nextgen.startup/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-1.5 rounded-full hover:bg-white/10 text-white hover:text-[#FF6B6B] transition-colors duration-300"
              aria-label="Follow us on Instagram"
            >
              <Instagram size={20} />
            </a>
            <a 
              href="mailto:hello@nextgenstartup.ai"
              className="p-1.5 rounded-full hover:bg-white/10 text-white hover:text-[#FF6B6B] transition-colors duration-300"
              aria-label="Email us"
            >
              <Mail size={20} />
            </a>
            <a 
              href="tel:+9613102322"
              className="p-1.5 rounded-full hover:bg-white/10 text-white hover:text-[#FF6B6B] transition-colors duration-300"
              aria-label="Call us"
            >
              <PhoneCall size={20} />
            </a>
          </div>
        </div>
        
        {/* Navigation Links */}
        <div className="flex flex-col">
          <h3 className="text-white font-medium text-lg mb-4">Quick Links</h3>
          <ul className="space-y-2">
            {quickLinks.map(link => (
              <li key={link.id}>
                <button
                  onClick={() => scrollToSection(link.id)}
                  className="text-white/80 hover:text-[#FF6B6B] transition-colors text-sm"
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Contact info */}
        <div className="flex flex-col">
          <h3 className="text-white font-medium text-lg mb-4">Contact Us</h3>
          <div className="space-y-2">
            <p className="text-white/80 text-sm flex items-center">
              <Mail size={16} className="mr-2" />
              <a href="mailto:hello@nextgenstartup.ai" className="hover:text-[#FF6B6B] transition-colors">
                hello@nextgenstartup.ai
              </a>
            </p>
            <p className="text-white/80 text-sm flex items-center">
              <PhoneCall size={16} className="mr-2" />
              <a href="tel:+9613102322" className="hover:text-[#FF6B6B] transition-colors">
                +961 3 102 322
              </a>
            </p>
            <div className="mt-4">
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-[#FF6B6B] text-white px-4 py-2 rounded hover:bg-[#FF5252] transition-all duration-300 shadow-sm hover:shadow font-bold"
              >
                Let's Talk
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright section */}
      <div className="border-t border-white/10 mt-8 pt-6 text-center">
        <p className="text-white/70 text-sm">
          © {new Date().getFullYear()} NextGen Startup. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
